/**
 * clusterResolution — Doc 12 §5.1 / Rule 1, as a pure plan.
 *
 * A Cluster (duplicate | variant) is a claim of identity. Rule 1: "cluster
 * review must not silently collapse items" and "cluster resolution must
 * explicitly decide whether the right outcome is duplicate, variant, or
 * item-native reframe". This module turns that decision into the exact
 * writes, so the UI explains and neither guesses. The writes themselves run
 * as ONE server transaction — `resolve_item_cluster` (migration 749) applies
 * this same plan, confirms the group and records the Rule 1 Decision, or
 * changes nothing; the store only calls it.
 *
 *   duplicate — the non-canonical members stop being prioritization objects:
 *               each closes with a `duplicate_closed` closure record pointing
 *               at the canonical AND a `supersede` lineage event (Rule 2:
 *               structural transformations always produce lineage).
 *   variant   — nothing closes; the cluster is confirmed as kinship worth
 *               keeping. Both remain individually rankable (§5.1).
 *   reframe   — the non-canonical members close with a
 *               `reframed_to_canonical` closure record; Rule 1 says NO
 *               lineage row is created solely because of a reframe. The
 *               caller then opens the canonical for rewording.
 *
 * Pure: no stores, no React. Tested in clusterResolution.test.ts.
 */

export type ClusterResolutionOutcome = 'duplicate' | 'variant' | 'reframe';

export interface ResolvableGroup {
  id: string;
  type: string;
  status: string;
  subtype?: string | null;
}

export interface ResolvableMembership {
  groupId: string;
  itemId: string;
  membershipRole?: 'canonical' | 'member' | 'optional' | null;
}

export interface ResolvableItem {
  id: string;
  exit_status?: string | null;
  dedupeStatus?: string | null;
  duplicateOfId?: string | null;
}

export interface ClusterClosurePlan {
  itemId: string;
  pointerItemId: string;
  closureType: 'duplicate_closed' | 'reframed_to_canonical';
  /** Rule 2 — duplicate is a structural transformation; reframe is not. */
  withLineage: boolean;
}

export interface ClusterResolutionPlan {
  outcome: ClusterResolutionOutcome;
  groupId: string;
  /** Present for duplicate and reframe; absent for variant. */
  canonicalItemId?: string;
  /** Items that stop being prioritization objects. Empty for variant. */
  closures: ClusterClosurePlan[];
  /** Members that stay live; their dedupe status becomes 'unique'. */
  survivorItemIds: string[];
  groupUpdate: { status: 'confirmed'; subtype: 'duplicate' | 'variant' };
}

export type ClusterResolutionResult =
  | { ok: true; plan: ClusterResolutionPlan }
  | { ok: false; reason: string };

export const OUTCOME_LABELS: Record<ClusterResolutionOutcome, { label: string; summary: string }> = {
  duplicate: {
    label: 'Duplicate — keep one',
    summary: 'The other items close and point at the one you keep. Only the kept item enters pools and rankings.',
  },
  variant: {
    label: 'Variant — keep all',
    summary: 'They share a core but differ in a way worth keeping. All stay individually rankable; the cluster records the kinship.',
  },
  reframe: {
    label: 'Same item, another category — keep one, reword it',
    summary: 'Neither wording is right. The others close and point at the one you keep, which you then reword.',
  },
};

/** The member the dialog should preselect as canonical, if any. */
export function defaultCanonicalItemId(memberships: readonly ResolvableMembership[]): string | undefined {
  return memberships.find((m) => m.membershipRole === 'canonical')?.itemId;
}

/** Is this cluster still awaiting its Rule 1 decision? */
export function isUnresolvedCluster(group: ResolvableGroup): boolean {
  return group.type === 'cluster' && (group.status === 'candidate' || group.status === 'active');
}

/**
 * A closed duplicate: an item that a cluster resolution (or an older
 * supersede) has pointed at a canonical. Pool pickers hide these — the
 * canonical is the one thing to rank.
 */
export function isClosedDuplicate(item: ResolvableItem): boolean {
  if (item.dedupeStatus === 'duplicate_of') return true;
  return item.exit_status === 'archived' && !!item.duplicateOfId;
}

export function planClusterResolution(args: {
  group: ResolvableGroup;
  memberships: readonly ResolvableMembership[];
  outcome: ClusterResolutionOutcome;
  canonicalItemId?: string;
}): ClusterResolutionResult {
  const { group, outcome, canonicalItemId } = args;
  if (group.type !== 'cluster') return { ok: false, reason: 'Only a cluster can be resolved this way.' };
  if (group.status === 'dissolved') return { ok: false, reason: 'This cluster was dissolved.' };
  if (group.status === 'confirmed') return { ok: false, reason: 'This cluster is already resolved.' };

  const memberIds = args.memberships.filter((m) => m.groupId === group.id).map((m) => m.itemId);
  const uniqueMemberIds = [...new Set(memberIds)];
  if (uniqueMemberIds.length < 2) return { ok: false, reason: 'A cluster needs at least two members to resolve.' };

  if (outcome === 'variant') {
    return {
      ok: true,
      plan: {
        outcome,
        groupId: group.id,
        closures: [],
        survivorItemIds: uniqueMemberIds,
        groupUpdate: { status: 'confirmed', subtype: 'variant' },
      },
    };
  }

  if (!canonicalItemId) return { ok: false, reason: 'Choose which item to keep.' };
  if (!uniqueMemberIds.includes(canonicalItemId)) return { ok: false, reason: 'The item to keep must be a member of the cluster.' };

  const closureType = outcome === 'duplicate' ? 'duplicate_closed' : 'reframed_to_canonical';
  const closures: ClusterClosurePlan[] = uniqueMemberIds
    .filter((id) => id !== canonicalItemId)
    .map((itemId) => ({ itemId, pointerItemId: canonicalItemId, closureType, withLineage: outcome === 'duplicate' }));

  return {
    ok: true,
    plan: {
      outcome,
      groupId: group.id,
      canonicalItemId,
      closures,
      survivorItemIds: [canonicalItemId],
      groupUpdate: { status: 'confirmed', subtype: 'duplicate' },
    },
  };
}
